import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface HelpTooltipProps {
  content: string;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  size?: "sm" | "md" | "lg";
}

export const HelpTooltip = ({ 
  content, 
  className,
  side = "top",
  size = "sm"
}: HelpTooltipProps) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button 
            type="button"
            className={cn(
              "inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-help",
              className
            )}
          >
            <HelpCircle className={sizeClasses[size]} />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-sm">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Hook to get translated help content for Nest Points system
export const useNestPointsHelp = () => {
  const { t } = useTranslation();
  
  return {
    // Policy tab
    policyName: t("helpTooltips.policyName", "The name of your Nest Points policy, visible to employees."),
    attributionModel: t("helpTooltips.attributionModel", "Choose how points are distributed: uniformly to all or by seniority level."),
    basePointsPerWeek: t("helpTooltips.basePointsPerWeek", "The base point cost for one week of workation in the same timezone."),
    maximumBalance: t("helpTooltips.maximumBalance", "The maximum Nest Points balance an employee can accumulate."),
    monthlyPointsCredit: t("helpTooltips.monthlyPointsCredit", "The number of points automatically credited each month to all employees."),
    
    // Seniority levels
    seniorityLevel: t("helpTooltips.seniorityLevel", "Define seniority levels (L2-L5) to attribute different monthly points."),
    seniorityExample: t("helpTooltips.seniorityExample", "Example: L2 = 5 pts/month, L3 = 10 pts/month, L4 = 15 pts/month, L5 = 20 pts/month"),
    
    // Timezone/Distance
    timezoneRestriction: t("helpTooltips.timezoneRestriction", "Limit accessible destinations based on timezone offset from headquarters."),
    distancePricing: t("helpTooltips.distancePricing", "Apply a point multiplier based on the destination's distance (timezone)."),
    distanceExample: t("helpTooltips.distanceExample", "Example: same timezone = x1, ±2h = x1.5, ±4h = x2, beyond = x2.5"),
    headquartersTimezone: t("helpTooltips.headquartersTimezone", "The reference timezone for calculating destination distances."),
    
    // Employee points
    bonusPoints: t("helpTooltips.bonusPoints", "Add bonus points to an employee to reward exceptional performance."),
    currentBalance: t("helpTooltips.currentBalance", "The employee's current balance, including monthly credits and bonuses."),
    
    // Calculator
    workationCalculator: t("helpTooltips.workationCalculator", "Simulate the point cost of a workation based on destination and duration."),
    
    // Employee guide
    howPointsWork: t("helpTooltips.howPointsWork", "Nest Points are credited monthly and allow you to fund your workations."),
    planningBudget: t("helpTooltips.planningBudget", "Check your balance and destination costs to plan your next trip."),
    pointCosts: t("helpTooltips.pointCosts", "Costs vary by distance: the further the destination, the more points it costs."),
  };
};

// Legacy static export for backwards compatibility (will use English defaults)
export const NestPointsHelp = {
  policyName: "The name of your Nest Points policy, visible to employees.",
  attributionModel: "Choose how points are distributed: uniformly to all or by seniority level.",
  basePointsPerWeek: "The base point cost for one week of workation in the same timezone.",
  maximumBalance: "The maximum Nest Points balance an employee can accumulate.",
  monthlyPointsCredit: "The number of points automatically credited each month to all employees.",
  seniorityLevel: "Define seniority levels (L2-L5) to attribute different monthly points.",
  seniorityExample: "Example: L2 = 5 pts/month, L3 = 10 pts/month, L4 = 15 pts/month, L5 = 20 pts/month",
  timezoneRestriction: "Limit accessible destinations based on timezone offset from headquarters.",
  distancePricing: "Apply a point multiplier based on the destination's distance (timezone).",
  distanceExample: "Example: same timezone = x1, ±2h = x1.5, ±4h = x2, beyond = x2.5",
  headquartersTimezone: "The reference timezone for calculating destination distances.",
  bonusPoints: "Add bonus points to an employee to reward exceptional performance.",
  currentBalance: "The employee's current balance, including monthly credits and bonuses.",
  workationCalculator: "Simulate the point cost of a workation based on destination and duration.",
  howPointsWork: "Nest Points are credited monthly and allow you to fund your workations.",
  planningBudget: "Check your balance and destination costs to plan your next trip.",
  pointCosts: "Costs vary by distance: the further the destination, the more points it costs.",
};